'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { Search, X, BookOpen, ChevronLeft, Filter, ChevronDown, ChevronUp, Loader2, Bookmark } from 'lucide-react';
import Link from 'next/link';
import BookBadge, { BookType } from '@/components/BookBadge';
import { searchIslamicTexts, SearchResultItem } from '@/lib/typesense';

import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logEvent } from '@/lib/analytics';

const BOOK_FILTER_OPTIONS: { key: string; label: string; value: string }[] = [
  { key: 'filter-all', label: 'All Books', value: 'all' },
  { key: 'filter-tafsir', label: 'Tafsir', value: 'Tafsir of Quran' },
  { key: 'filter-prophets', label: 'Prophets', value: 'Stories of Prophets' },
  { key: 'filter-bukhari', label: 'Bukhari', value: 'Sahih Bukhari' },
  { key: 'filter-muslim', label: 'Muslim', value: 'Sahih Muslim' },
];

const BOOK_TYPE_MAP: Record<string, BookType> = {
  'tafsir of quran': 'tafsir',
  'stories of prophets': 'prophets',
  'sahih bukhari': 'bukhari',
  'sahih muslim': 'muslim',
};

function getBookType(book: string): BookType {
  const key = book?.toLowerCase();
  return BOOK_TYPE_MAP[key] || 'bukhari';
}

const TOP_RESULTS_COUNT = 30;
const PREVIEW_LENGTH = 300;
const FULL_LENGTH = 2000;

function ResultCard({ r, index, user, supabase }: { r: SearchResultItem; index: number; user: any; supabase: any }) {
  const [expanded, setExpanded] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState<string | null>(null);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const bookType = getBookType(r.book);
  const preview = r.text.slice(0, PREVIEW_LENGTH);
  const fullText = r.text.slice(0, FULL_LENGTH);
  const hasMore = r.text.length > PREVIEW_LENGTH;

  // Check if already bookmarked on mount (for signed-in users)
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from('saved_items')
        .select('id')
        .eq('user_id', user.id)
        .eq('item_type', 'passage')
        .eq('content', r.text.slice(0, FULL_LENGTH))
        .maybeSingle();
      if (data) {
        setBookmarked(true);
        setBookmarkId(data.id);
      }
    })();
  }, [user, r.text]);

  const handleBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    setBookmarkLoading(true);
    try {
      if (bookmarked && bookmarkId) {
        await supabase.from('saved_items').delete().eq('id', bookmarkId);
        setBookmarked(false);
        setBookmarkId(null);
      } else {
        const { data, error } = await supabase.from('saved_items').insert({
          user_id: user.id,
          content: r.text.slice(0, FULL_LENGTH),
          item_type: 'passage',
          book: bookType,
          title: r.title || null,
          reference: r.reference || null,
          source: r.book || 'Search',
        }).select('id').single();
        if (!error && data) {
          setBookmarked(true);
          setBookmarkId(data.id);
          logEvent('item_saved');
        }
      }
    } finally {
      setBookmarkLoading(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 hover:border-accent/30 transition-all duration-200 animate-fade-in-up cursor-pointer"
      style={{ animationDelay: `${index * 50}ms` }}
      onClick={() => hasMore && setExpanded((v) => !v)}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <BookBadge book={bookType} size="sm" />
        </div>
        {user && (
          <button
            onClick={handleBookmark}
            disabled={bookmarkLoading}
            className={`p-1.5 rounded-xl transition-all duration-150 active:scale-95 ${
              bookmarked
                ? 'text-accent bg-accent/10' :'text-muted-foreground hover:text-accent hover:bg-accent/10'
            }`}
            aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark passage'}
          >
            {bookmarkLoading ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Bookmark size={15} fill={bookmarked ? 'currentColor' : 'none'} />
            )}
          </button>
        )}
      </div>
      {r.title ? (
        <p className="text-sm font-semibold text-foreground mb-1.5 leading-snug">{r.title}</p>
      ) : null}
      <p className="text-sm text-muted-foreground leading-relaxed">
        {expanded ? fullText : preview}
        {!expanded && hasMore && (
          <span className="text-accent font-medium">…</span>
        )}
        {expanded && r.text.length > FULL_LENGTH && (
          <span className="text-accent font-medium">…</span>
        )}
      </p>
      {hasMore && (
        <button
          className="mt-2 flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        >
          {expanded ? (
            <><ChevronUp size={13} /> Show less</>
          ) : (
            <><ChevronDown size={13} /> Show more</>
          )}
        </button>
      )}
    </div>
  );
}

function SearchResultsContentInner() {
  const searchParams = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const initialBook = searchParams.get('book') || 'all';
  const { user } = useAuth();
  const supabase = createClient();

  const [query, setQuery] = useState(initialQ);
  const [activeBook, setActiveBook] = useState<string>(initialBook);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [rateLimitError, setRateLimitError] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const runSearch = useCallback(
    async (q: string, book: string) => {
      if (!q.trim()) return;
      setIsSearching(true);
      setRateLimitError(false);
      try {
        const data = await searchIslamicTexts(q, book === 'all' ? undefined : book);
        setResults(data);
        logEvent('search_performed');
      } catch (err: unknown) {
        if (err instanceof Error && err.message === 'RATE_LIMITED') {
          setRateLimitError(true);
        }
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  useEffect(() => {
    if (initialQ) runSearch(initialQ, initialBook);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(query, activeBook);
  };

  const handleBookFilter = (book: string) => {
    setActiveBook(book);
    if (query.trim()) runSearch(query, book);
  };

  return (
    <div className="max-w-lg mx-auto px-4">
      {/* Header */}
      <div className="flex items-center gap-3 pt-12 pb-4">
        <Link
          href="/"
          className="p-2 rounded-xl hover:bg-muted transition-colors"
          aria-label="Back to home"
        >
          <ChevronLeft size={20} className="text-muted-foreground" />
        </Link>
        <h1 className="text-lg font-bold text-foreground flex-1">Search</h1>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-4">
        <div
          className="flex items-center gap-2 px-3 py-2.5 rounded-2xl border border-border focus-within:border-accent/60 transition-all duration-200"
          style={{ background: 'var(--card)' }}
        >
          <Search size={16} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Islamic texts…"
            className="flex-1 bg-transparent text-sm text-foreground placeholder-muted-foreground outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus(); }}
              className="p-1 rounded-lg hover:bg-muted transition-colors"
              aria-label="Clear search"
            >
              <X size={14} className="text-muted-foreground" />
            </button>
          )}
          <button
            type="submit"
            disabled={!query.trim() || isSearching}
            className="px-3 py-1.5 rounded-xl bg-accent text-accent-foreground text-xs font-semibold disabled:opacity-40 hover:opacity-90 transition-all duration-150 active:scale-95"
          >
            {isSearching ? <Loader2 size={13} className="animate-spin" /> : 'Search'}
          </button>
        </div>
      </form>

      {/* Book filter chips */}
      <div className="flex items-center gap-2 mb-5 overflow-x-auto scrollbar-hide pb-1">
        <Filter size={13} className="text-muted-foreground shrink-0" />
        {BOOK_FILTER_OPTIONS.map(({ key, label, value }) => (
          <button
            key={key}
            onClick={() => handleBookFilter(value)}
            className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
              activeBook === value
                ? 'bg-accent text-accent-foreground border-accent'
                : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Rate limit error */}
      {rateLimitError && (
        <div className="rounded-2xl border border-red-400/30 bg-red-400/5 p-4 mb-4 text-center">
          <p className="text-sm font-semibold text-red-400 mb-1">Too many searches</p>
          <p className="text-xs text-muted-foreground">Please wait a moment before searching again.</p>
        </div>
      )}

      {/* Results */}
      {isSearching ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={`sk-${i}`} className="rounded-2xl border border-border bg-card p-4 h-28 skeleton-shimmer" />
          ))}
        </div>
      ) : results.length > 0 ? (
        <>
          <p className="text-xs text-muted-foreground mb-3">
            <span className="text-foreground font-semibold">{results.length}</span> result{results.length !== 1 ? 's' : ''} found
          </p>
          <div className="space-y-3 pb-24">
            {results.map((r, i) => (
              <ResultCard key={`${r.id}-${i}`} r={r} index={i} user={user} supabase={supabase} />
            ))}
          </div>
        </>
      ) : query && !isSearching ? (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <BookOpen size={28} className="text-muted-foreground" />
          </div>
          <h2 className="text-base font-bold text-foreground mb-2">No results found</h2>
          <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
            Try different keywords or browse a specific book using the filters above.
          </p>
        </div>
      ) : !query ? (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <Search size={28} className="text-muted-foreground" />
          </div>
          <h2 className="text-base font-bold text-foreground mb-2">Search Islamic texts</h2>
          <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
            Search across Tafsir, Stories of Prophets, Sahih Bukhari, and Sahih Muslim.
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default function SearchResultsContent() {
  return (
    <React.Suspense
      fallback={
        <div className="max-w-lg mx-auto px-4 pt-12">
          <div className="skeleton-shimmer h-10 w-full rounded-xl mb-4" />
        </div>
      }
    >
      <SearchResultsContentInner />
    </React.Suspense>
  );
}